import { Check, EyeOff, ExternalLink, Maximize2, MessageCircle, Mic, MicOff, Minimize2, MonitorUp, RefreshCw, ShieldCheck, Square, Volume2, VolumeX, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import type { DesktopCaptureSource, LiveControllerState, ShareQuality } from "./desktop";
import { HlsVideo, type WatchResponse } from "./liveShare";
import { useRealtime } from "./realtime";
import type { Message } from "./types";

export function DetachedLiveViewerWindow({ streamId: explicitId, chatRoute: explicitRoute, onClose }: {
  streamId?: string; chatRoute?: string; onClose?: () => void;
}) {
  const params = new URLSearchParams(window.location.search);
  const streamId = explicitId || params.get("streamId") || "";
  const route = explicitRoute || params.get("chatRoute") || "/comms";
  const chatRoute = /^\/(dm|channel)\/[^/?#]+$/.test(route) ? route : "/comms";
  const channelRouteMatch = /^\/channel\/([^/?#]+)$/.exec(chatRoute);
  const channelSlug = channelRouteMatch ? decodeURIComponent(channelRouteMatch[1]) : "";
  const [watching, setWatching] = useState<WatchResponse | null>(null);
  const [error, setError] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const desktop = window.verseDesktop;
  useEffect(() => {
    let disposed = false;
    let heartbeat: number | undefined;
    let joined = false;
    let left = false;
    const leave = () => {
      disposed = true;
      window.clearInterval(heartbeat);
      if (joined && !left) {
        left = true;
        void api(`/live/${encodeURIComponent(streamId)}/leave`, { method: "POST", keepalive: true }).catch(() => undefined);
      }
    };
    if (!streamId) { setError("No broadcast was selected."); return; }
    setWatching(null); setError("");
    void api<WatchResponse>(`/live/${encodeURIComponent(streamId)}/watch`, { method: "POST" }).then(result => {
      joined = true;
      if (disposed) { leave(); return; }
      setWatching(result);
      heartbeat = window.setInterval(() => {
        void api(`/live/${encodeURIComponent(streamId)}/heartbeat`, { method: "POST" })
          .catch(() => { if (!disposed) { setError("This broadcast is no longer available."); setWatching(null); leave(); } });
      }, 15_000);
    }).catch(reason => { if (!disposed) setError(reason instanceof Error ? reason.message : "Could not open live stream."); });
    window.addEventListener("pagehide", leave);
    return () => { leave(); window.removeEventListener("pagehide", leave); };
  }, [streamId]);
  const close = () => { if (onClose) onClose(); else if (desktop) desktop.closeLiveViewer(); else window.close(); };
  return <main className="detached-live-viewer">
    <header className="detached-live-titlebar"><div><i className="live-status-dot" /><strong>{watching ? `${watching.stream.ownerName} · ${watching.stream.sourceLabel}` : "LIVE"}</strong></div>
      <div className="detached-live-window-actions">
        {desktop && !onClose ? <><button onClick={desktop.minimizeLiveViewer} aria-label="Minimize viewer"><Minimize2 size={15} /></button><button onClick={desktop.toggleMaximizeLiveViewer} aria-label="Maximize viewer"><Maximize2 size={15} /></button></> : null}
        <button onClick={close} aria-label="Close live viewer"><X size={16} /></button>
      </div>
    </header>
    <section className="detached-live-video">{watching?.media ? <HlsVideo media={watching.media} label={watching.stream.ownerName} /> : <div className="detached-live-message" role="status">{error || "Starting stream…"}</div>}{chatOpen && channelSlug ? <LiveChatOverlay slug={channelSlug} onClose={() => setChatOpen(false)} /> : null}</section>
    <footer className="detached-live-footer"><div><i className="live-status-dot" /><span>LIVE · 720p max</span></div><button aria-expanded={channelSlug ? chatOpen : undefined} onClick={() => channelSlug ? setChatOpen(open => !open) : (() => {
      if (desktop) desktop.showMainWindow(chatRoute);
      else if (onClose) onClose();
      else if (window.opener && !window.opener.closed) { window.opener.focus(); }
      else window.open(chatRoute, "_blank", "noopener");
    })()}><MessageCircle size={15} /> {chatOpen ? "Hide Chat" : channelSlug ? "Open Chat" : "Open Channel"}</button></footer>
  </main>;
}

function messageKey(message: Message) { return message._id || message.id || `${message.createdAt || ""}:${message.username || ""}:${message.text || ""}`; }

function upsertLiveMessage(messages: Message[], message: Message) {
  const key = messageKey(message);
  const next = [...messages];
  const index = next.findIndex(item => messageKey(item) === key);
  if (index >= 0) next[index] = { ...next[index], ...message };
  else next.push(message);
  return next.sort((a, b) => Date.parse(a.createdAt || "") - Date.parse(b.createdAt || "")).slice(-100);
}

function LiveChatOverlay({ slug, onClose }: { slug: string; onClose: () => void }) {
  const auth = useAuth();
  const { joinChannel, subscribe, send } = useRealtime();
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    joinChannel(slug);
    const unsubscribe = subscribe(payload => {
      if ((payload.type === "channel_message" || payload.type === "channel_message_updated" || payload.type === "channel_reaction_update") && payload.message && (payload.channel === slug || payload.message.channelSlug === slug)) {
        setMessages(current => upsertLiveMessage(current, payload.message as Message));
      } else if (payload.type === "channel_message_deleted" && payload.channel === slug && payload.messageId) {
        setMessages(current => current.map(message => messageKey(message) === payload.messageId ? { ...message, text: "", attachments: [], deletedAt: new Date().toISOString() } : message));
      }
    });
    setLoading(true);
    void api<{ messages: Message[] }>(`/channels/${encodeURIComponent(slug)}/messages?limit=100`).then(result => {
      if (active) setMessages(current => [...(result.messages || []).reduce((items, message) => upsertLiveMessage(items, message), [] as Message[]), ...current].reduce((items, message) => upsertLiveMessage(items, message), [] as Message[]));
    }).catch(reason => { if (active) setError(reason instanceof Error ? reason.message : "Could not load channel chat."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; unsubscribe(); };
  }, [joinChannel, subscribe, slug]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages.length]);

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending || !auth.user) return;
    setSending(true); setError("");
    try {
      const message = await api<Message>(`/channels/${encodeURIComponent(slug)}/messages`, { method: "POST", body: JSON.stringify({ text, type: "TEXT", clientMessageId: crypto.randomUUID() }) });
      setMessages(current => upsertLiveMessage(current, message));
      setDraft("");
      send({ type: "typing", channelSlug: slug, typing: false });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Message could not be sent.");
    } finally { setSending(false); }
  }

  return <aside className="live-chat-overlay" aria-label="Live channel chat">
    <header className="live-chat-heading"><span><MessageCircle size={14} /><strong>LIVE CHAT</strong></span><button type="button" onClick={onClose} aria-label="Hide chat"><X size={14} /></button></header>
    <div className="live-chat-messages" ref={listRef} aria-live="polite" aria-relevant="additions text">
      {loading && !messages.length ? <p className="live-chat-state">Connecting to channel chat…</p> : null}
      {!loading && !messages.length && !error ? <p className="live-chat-state">Be the first to say something.</p> : null}
      {messages.map(message => <article className={`live-chat-message ${message.userId === auth.user?.id || message.fromUserId === auth.user?.id ? "mine" : ""}`} key={messageKey(message)}>
        <strong>{message.deletedAt ? "Message removed" : message.displayName || message.from?.displayName || message.username || "Pilot"}</strong>
        {message.deletedAt ? null : <span>{message.text || (message.attachments?.length ? "Shared an attachment" : "")}</span>}
      </article>)}
      {error ? <p className="live-chat-error" role="alert">{error}</p> : null}
    </div>
    <form className="live-chat-composer" onSubmit={sendMessage}>
      <textarea aria-label="Write a channel message" rows={1} value={draft} disabled={!auth.user || sending} placeholder={auth.loading ? "Signing in…" : auth.user ? "Send a message…" : "Sign in to chat"} onChange={event => setDraft(event.target.value)} onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void sendMessage(event); } }} />
      <button type="submit" aria-label="Send message" disabled={!draft.trim() || sending || !auth.user}><MessageCircle size={15} /></button>
    </form>
  </aside>;
}

export function DetachedShareControllerWindow() {
  const [state, setState] = useState<LiveControllerState | null>(null);
  const [minimized, setMinimized] = useState(false);
  const bubbleDrag = useRef({ dragging: false, moved: false, x: 0, y: 0 });
  useEffect(() => {
    const bridge = window.verseDesktop;
    let active = true;
    const dispose = bridge?.onLiveControllerState(setState);
    const disposeMinimized = bridge?.onLiveControllerMinimized(setMinimized);
    void bridge?.getLiveControllerState().then(next => { if (active) setState(next); });
    return () => { active = false; dispose?.(); disposeMinimized?.(); };
  }, []);
  useEffect(() => {
    document.body.classList.toggle("share-bubble-mode", minimized);
    return () => document.body.classList.remove("share-bubble-mode");
  }, [minimized]);
  if (minimized) return <button className="detached-share-bubble" aria-label="Open live sharing controls"
    onPointerDown={event => { bubbleDrag.current = { dragging: true, moved: false, x: event.screenX, y: event.screenY }; event.currentTarget.setPointerCapture(event.pointerId); }}
    onPointerMove={event => { const drag = bubbleDrag.current; if (!drag.dragging) return; const dx = event.screenX - drag.x; const dy = event.screenY - drag.y; if (Math.abs(dx) + Math.abs(dy) < 2) return; drag.moved = true; drag.x = event.screenX; drag.y = event.screenY; window.verseDesktop?.moveShareController({ dx, dy }); }}
    onPointerUp={event => { bubbleDrag.current.dragging = false; event.currentTarget.releasePointerCapture(event.pointerId); }}
    onClick={() => { if (bubbleDrag.current.moved) { bubbleDrag.current.moved = false; return; } setMinimized(false); window.verseDesktop?.expandShareController(); }}><i className="detached-share-recording-dot" /><span>LIVE</span></button>;
  return <main className="detached-share-controller">
    <header className="detached-share-titlebar"><div><i className="live-status-dot" /><strong>YOU'RE LIVE</strong></div><div className="detached-share-title-actions"><button aria-label="Open VerseComms" onClick={() => window.verseDesktop?.showMainWindow()}><ExternalLink size={14} /></button><button aria-label="Minimize sharing controls" onClick={() => window.verseDesktop?.minimizeShareController()}><Minimize2 size={15} /></button></div></header>
    {state ? <section className="detached-share-content">
      <div className="detached-share-source"><div><strong>{state.sourceLabel}</strong><span>{state.audioLabel || "Computer audio on"} · Not recorded</span></div><span className="detached-quality-chip">{state.qualityLabel}</span></div>
      <div className="detached-share-stats"><div><span>Watching</span><strong>{state.viewerCount}</strong></div><div><span>Upload</span><strong>{state.bitrateLabel || "—"}</strong></div><div><span>Status</span><strong>{state.statusLabel || "Live"}</strong></div></div>
      <div className="detached-share-audio-mixer">
        <ControllerAudioLevel kind="computer" available={Boolean(state.computerAudioAvailable)} enabled={Boolean(state.computerAudioEnabled)} volume={state.computerAudioVolume || 0} />
        <ControllerAudioLevel kind="microphone" available={Boolean(state.microphoneAvailable)} enabled={Boolean(state.microphoneEnabled)} volume={state.microphoneVolume || 0} />
      </div>
      <div className="detached-share-actions"><button onClick={() => window.verseDesktop?.sendLiveControllerAction("change-source")}><MonitorUp size={15} /> Change Source</button><button className="danger" onClick={() => window.verseDesktop?.sendLiveControllerAction("stop")}><Square size={14} /> Stop Sharing</button></div>
    </section> : <p role="status">Opening sharing controls…</p>}
  </main>;
}

function ControllerAudioLevel({ kind, available, enabled, volume }: { kind: "computer" | "microphone"; available: boolean; enabled: boolean; volume: number }) {
  const action = kind === "computer" ? "computer-volume" : "microphone-volume";
  const label = kind === "computer" ? "Computer" : "Mic";
  return <div className={enabled ? "enabled" : ""}>
    <button type="button" disabled={!available} onClick={() => window.verseDesktop?.sendLiveControllerAction(action, enabled && volume > 0 ? 0 : 100)} aria-label={available ? `${enabled ? "Mute" : "Unmute"} ${label.toLowerCase()}` : `${label} was not added to this share`}>
      {kind === "computer" ? (enabled ? <Volume2 size={14} /> : <VolumeX size={14} />) : (enabled ? <Mic size={14} /> : <MicOff size={14} />)}
      <span>{available ? label : `${label} off`}</span>
    </button>
    <input type="range" min="0" max="100" step="1" value={available ? volume : 0} disabled={!available} onChange={event => window.verseDesktop?.sendLiveControllerAction(action, Number(event.target.value))} aria-label={`${label} volume`} />
    <output>{enabled ? volume : 0}%</output>
  </div>;
}

const PICKER_QUALITIES: Array<{ value: ShareQuality; label: string; detail: string }> = [
  { value: "auto", label: "Auto", detail: "Adapts up to 720p" },
  { value: "480p", label: "480p", detail: "Lower bandwidth" },
  { value: "720p", label: "720p", detail: "Sharper text" },
];

function AudioLevelControl({ kind, enabled, volume, onEnabled, onVolume }: { kind: "computer" | "microphone"; enabled: boolean; volume: number; onEnabled: (value: boolean) => void; onVolume: (value: number) => void }) {
  const label = kind === "computer" ? "Computer audio" : "Microphone";
  return <div className={`share-picker-audio-control ${enabled ? "selected" : ""}`}>
    <button type="button" onClick={() => onEnabled(!enabled)} aria-pressed={enabled} aria-label={`${enabled ? "Mute" : "Turn on"} ${label.toLowerCase()}`}>
      {kind === "computer" ? (enabled ? <Volume2 size={18} /> : <VolumeX size={18} />) : (enabled ? <Mic size={18} /> : <MicOff size={18} />)}
      <span><b>{label}</b><small>{enabled ? `${volume}% · Click icon to mute` : "Muted"}</small></span><i><span /></i>
    </button>
    <input type="range" min="0" max="100" step="1" value={enabled ? volume : 0} disabled={!enabled} onChange={event => onVolume(Number(event.target.value))} aria-label={`${label} volume`} />
  </div>;
}

export function DetachedSharePickerWindow() {
  const [sources, setSources] = useState<DesktopCaptureSource[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [quality, setQuality] = useState<ShareQuality>("auto");
  const [includeComputerAudio, setIncludeComputerAudio] = useState(true);
  const [computerAudioVolume, setComputerAudioVolume] = useState(100);
  const [includeMicrophone, setIncludeMicrophone] = useState(false);
  const [microphoneVolume, setMicrophoneVolume] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const selected = sources.find(source => source.id === selectedId) || null;

  const loadSources = async () => {
    setLoading(true);
    setError("");
    try {
      const next = await window.verseDesktop?.getCaptureSources() || [];
      setSources(next);
      setSelectedId(current => next.some(source => source.id === current) ? current : next[0]?.id || "");
      if (!next.length) setError("No screens or windows are available to share.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not load screens and windows.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadSources(); }, []);
  const close = () => window.verseDesktop?.closeSharePicker();
  const submit = () => {
    if (!selected) return;
    window.verseDesktop?.submitSharePicker({ source: selected, quality, includeComputerAudio, computerAudioVolume, includeMicrophone, microphoneVolume });
  };

  return <main className="detached-share-picker">
    <header className="share-picker-titlebar">
      <div><MonitorUp size={17} /><span><strong>Share screen</strong><small>Choose what your crew can see</small></span></div>
      <button type="button" onClick={close} aria-label="Close screen sharing"><X size={17} /></button>
    </header>
    <div className="share-picker-layout">
      <section className="share-picker-sources" aria-label="Screens and windows">
        <div className="share-picker-section-heading"><span><b>Source</b><small>{sources.length} available</small></span><button type="button" onClick={() => void loadSources()} disabled={loading}><RefreshCw size={14} className={loading ? "spin" : ""} /> Refresh</button></div>
        {error ? <div className="share-picker-error" role="alert">{error}</div> : null}
        <div className="share-picker-grid">
          {sources.map(source => {
            const active = selectedId === source.id;
            return <button type="button" key={source.id} className={active ? "selected" : ""} onClick={() => { setSelectedId(source.id); setConfirming(false); }} aria-pressed={active}>
              <span className="share-picker-thumbnail">{source.thumbnailDataUrl ? <img src={source.thumbnailDataUrl} alt="" /> : <MonitorUp size={28} />}{active ? <i><Check size={13} /></i> : null}</span>
              <span className="share-picker-source-name"><b>{source.name}</b><small>{source.id.startsWith("screen:") ? "Entire display" : "Application window"}</small></span>
            </button>;
          })}
        </div>
        {loading && !sources.length ? <div className="share-picker-loading" role="status"><RefreshCw className="spin" size={20} /> Finding screens and windows…</div> : null}
      </section>
      <aside className="share-picker-settings">
        <div className="share-picker-section-heading"><span><b>Stream quality</b><small>Video only</small></span></div>
        <div className="share-picker-quality-list">
          {PICKER_QUALITIES.map(option => <button type="button" key={option.value} className={quality === option.value ? "selected" : ""} onClick={() => { setQuality(option.value); setConfirming(false); }} aria-pressed={quality === option.value}><i /> <span><b>{option.label}</b><small>{option.detail}</small></span>{quality === option.value ? <Check size={15} /> : null}</button>)}
        </div>
        <div className="share-picker-audio-heading">Audio mix</div>
        <AudioLevelControl kind="computer" enabled={includeComputerAudio} volume={computerAudioVolume} onEnabled={value => { setIncludeComputerAudio(value); setConfirming(false); }} onVolume={value => { setComputerAudioVolume(value); setConfirming(false); }} />
        <AudioLevelControl kind="microphone" enabled={includeMicrophone} volume={microphoneVolume} onEnabled={value => { setIncludeMicrophone(value); setConfirming(false); }} onVolume={value => { setMicrophoneVolume(value); setConfirming(false); }} />
        <div className="share-picker-privacy"><ShieldCheck size={18} /><span><b>You stay in control</b><small>Choose each audio source separately. Stop or mute at any time from the LIVE control or taskbar.</small></span></div>
        <div className="share-picker-upcoming"><EyeOff size={15} /><span>1080p and 4K are coming later.</span></div>
      </aside>
    </div>
    <footer className="share-picker-footer">
      <div>{selected ? <><span className="live-status-dot" /><span><small>Ready to share</small><strong>{selected.name}</strong></span></> : <span>Select a source to continue</span>}</div>
      <div><button type="button" className="secondary" onClick={close}>Cancel</button><button type="button" className="primary" disabled={!selected || loading} onClick={() => confirming ? submit() : setConfirming(true)}>{confirming ? "Confirm & go live" : "Continue"}</button></div>
    </footer>
    {confirming && selected ? <div className="share-picker-confirm" role="alertdialog" aria-modal="true" aria-labelledby="share-confirm-title">
      <section><span className="share-picker-confirm-icon"><MonitorUp size={24} /></span><div><small>Final check</small><h2 id="share-confirm-title">Share this {selected.id.startsWith("screen:") ? "display" : "window"}?</h2><p><strong>{selected.name}</strong> will be visible to everyone in this conversation. Computer audio: {includeComputerAudio ? `${computerAudioVolume}%` : "muted"}. Microphone: {includeMicrophone ? `${microphoneVolume}%` : "muted"}. Your pointer stays off.</p></div><div className="share-picker-confirm-actions"><button type="button" onClick={() => setConfirming(false)}>Back</button><button type="button" onClick={submit}>Go live</button></div></section>
    </div> : null}
  </main>;
}
